import { useEffect, useCallback } from "react";
import type { MediaItem } from "@/lib/metadata";

interface PhotoLightboxProps {
  photo: MediaItem | null;
  media: MediaItem[];
  onClose: () => void;
  onNavigate: (photo: MediaItem) => void;
}

function formatTimestamp(seconds: number): string {
  if (seconds < 0) {
    const mins = Math.ceil(Math.abs(seconds) / 60);
    return mins === 1 ? "1 min before dive" : `${mins} min before dive`;
  }
  const mins = Math.floor(seconds / 60);
  return `${mins} min into dive`;
}

export function PhotoLightbox({ photo, media, onClose, onNavigate }: PhotoLightboxProps) {
  const currentIndex = photo ? media.findIndex(m => m.filename === photo.filename) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < media.length - 1;

  const goToPrev = useCallback(() => {
    if (hasPrev) onNavigate(media[currentIndex - 1]);
  }, [hasPrev, currentIndex, media, onNavigate]);

  const goToNext = useCallback(() => {
    if (hasNext) onNavigate(media[currentIndex + 1]);
  }, [hasNext, currentIndex, media, onNavigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, goToPrev, goToNext]);

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Previous button */}
      <button
        onClick={(e) => { e.stopPropagation(); goToPrev(); }}
        disabled={!hasPrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/40 hover:text-white/80 disabled:opacity-0 transition-all text-4xl cursor-pointer disabled:cursor-default"
        aria-label="Previous photo"
      >
        ‹
      </button>

      {/* Next button */}
      <button
        onClick={(e) => { e.stopPropagation(); goToNext(); }}
        disabled={!hasNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/40 hover:text-white/80 disabled:opacity-0 transition-all text-4xl cursor-pointer disabled:cursor-default"
        aria-label="Next photo"
      >
        ›
      </button>

      <div
        className="relative max-w-5xl max-h-[90vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white text-2xl"
          aria-label="Close"
        >
          &times;
        </button>

        {/* Image or Video */}
        {photo.type === "video" ? (
          <video
            src={photo.filename}
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
            autoPlay
            loop
            muted
            playsInline
            controls
          />
        ) : (
          <img
            src={photo.filename}
            alt={photo.caption || "Dive photo"}
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
          />
        )}

        {/* Caption, timestamp, and original link */}
        <div className="mt-4 text-center text-white">
          {photo.caption && (
            <p className="text-lg mb-1">{photo.caption}</p>
          )}
          <p className="text-sm text-white/60">
            {formatTimestamp(photo.timestamp)}
          </p>
        </div>
      </div>
    </div>
  );
}
